import { useState, useEffect } from 'react';
import { GripVertical, Check } from 'lucide-react';
import { Card, CardContent, CardHeader } from 'src/@/components/ui/card';
import { Button } from 'src/@/components/ui/button';
import { Badge } from 'src/@/components/ui/badge';
import { Label } from 'src/@/components/ui/label';
import Iconify from 'src/components/Iconify';

import v2_actionGetLayoutConfig from 'src/actions/v2/crm/v2_actionGetLayoutConfig';
import v2_actionSalvarLayoutConfig from 'src/actions/v2/crm/v2_actionSalvarLayoutConfig';

const DEFAULT_SECOES = [
  { key: "negociacao", label: "Negociação", posicao: 0 },
  { key: "contatos", label: "Contatos", posicao: 1 },
  { key: "atividades", label: "Atividades", posicao: 2 },
  { key: "comentarios", label: "Comentários", posicao: 3 },
  { key: "campos_personalizados", label: "Campos Personalizados", posicao: 4 },
  { key: "realestate", label: "Imóvel", posicao: 5 }
];

const MODO_OPTIONS = [
  { value: 'accordion', label: 'Accordion', desc: 'Seções expansíveis empilhadas verticalmente', icon: 'eva:list-outline' },
  { value: 'tabs', label: 'Tabs Horizontais', desc: 'Navegação por abas horizontais no topo', icon: 'eva:browser-outline' }
];

export default function TabLayoutConfig({ cliente }) {
  const [modo, setModo] = useState('accordion');
  const [secoes, setSecoes] = useState(DEFAULT_SECOES);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    buscarConfig();
  }, []);

  async function buscarConfig() {
    try {
      const result = await v2_actionGetLayoutConfig(cliente._id);
      if (result.status === 200 && !result.data?.error) {
        const data = result.data.data;
        if (data.modo_visualizacao) setModo(data.modo_visualizacao);
        if (data.ordem_secoes?.length > 0) {
          const merged = DEFAULT_SECOES.map(def => {
            const found = data.ordem_secoes.find(s => s.key === def.key);
            return found ? { ...def, posicao: found.posicao } : def;
          }).sort((a, b) => a.posicao - b.posicao);
          setSecoes(merged);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function handleSalvar() {
    try {
      setSalvando(true);
      const secoesComPosicao = secoes.map((s, i) => ({ key: s.key, label: s.label, posicao: i }));
      await v2_actionSalvarLayoutConfig(cliente._id, {
        modo_visualizacao: modo,
        ordem_secoes: secoesComPosicao
      });
      setSalvo(true);
      setTimeout(() => setSalvo(false), 2000);
    } catch (error) {
      console.log(error);
    } finally {
      setSalvando(false);
    }
  }

  function handleDragStart(index) {
    setDragIndex(index);
  }

  function handleDragOver(e, index) {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const newSecoes = [...secoes];
    const item = newSecoes.splice(dragIndex, 1)[0];
    newSecoes.splice(index, 0, item);
    setSecoes(newSecoes);
    setDragIndex(index);
  }

  function handleDragEnd() {
    setDragIndex(null);
  }

  function moveItem(index, direction) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= secoes.length) return;
    const newSecoes = [...secoes];
    [newSecoes[index], newSecoes[newIndex]] = [newSecoes[newIndex], newSecoes[index]];
    setSecoes(newSecoes);
  }

  return (
    <div className="w-3/4 mt-4 space-y-6">
      {/* Modo de visualização */}
      <Card className="border border-gray-100 shadow-sm">
        <CardHeader className="py-4 border-b border-dashed border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Modo de Visualização</h2>
          <p className="text-sm text-gray-500 mt-0.5">Escolha como as seções do card de negócio serão exibidas</p>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 gap-3">
            {MODO_OPTIONS.map(option => (
              <button
                key={option.value}
                onClick={() => setModo(option.value)}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                  modo === option.value
                    ? 'border-[#7C3AED] bg-violet-50/50 shadow-sm shadow-[#7C3AED]/10'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                  modo === option.value ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  <Iconify icon={option.icon} width={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${modo === option.value ? 'text-[#7C3AED]' : 'text-gray-800'}`}>
                      {option.label}
                    </span>
                    {modo === option.value && (
                      <div className="w-4 h-4 rounded-full bg-[#7C3AED] flex items-center justify-center">
                        <Check size={10} className="text-white" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{option.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Ordem das seções */}
      <Card className="border border-gray-100 shadow-sm">
        <CardHeader className="py-4 border-b border-dashed border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Ordem das Seções</h2>
          <p className="text-sm text-gray-500 mt-0.5">Arraste ou use as setas para reordenar as seções do card</p>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-1.5">
            {secoes.map((secao, index) => (
              <div
                key={secao.key}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
                  dragIndex === index
                    ? 'border-[#7C3AED] bg-violet-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <GripVertical size={14} className="text-gray-400 shrink-0" />
                <Badge variant="outline" className="text-[10px] font-mono text-gray-400 border-gray-200 w-5 h-5 flex items-center justify-center p-0 shrink-0">
                  {index + 1}
                </Badge>
                <span className="text-sm font-medium text-gray-800 flex-1">{secao.label}</span>
                <div className="flex gap-0.5">
                  <button
                    onClick={() => moveItem(index, -1)}
                    disabled={index === 0}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Iconify icon="eva:arrow-up-outline" width={14} />
                  </button>
                  <button
                    onClick={() => moveItem(index, 1)}
                    disabled={index === secoes.length - 1}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Iconify icon="eva:arrow-down-outline" width={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Botão salvar */}
      <div className="flex justify-end">
        <Button
          onClick={handleSalvar}
          disabled={salvando}
          className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white px-6 shadow-sm shadow-[#7C3AED]/20"
        >
          {salvando ? 'Salvando...' : salvo ? 'Salvo!' : 'Salvar configuração'}
        </Button>
      </div>
    </div>
  );
}
